import React, { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { SurveyTemplateEditor } from './SurveyCatalogTemplateEditPage'

function unwrap(res) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
}

export default function SurveyMyTemplateEditPage() {
  const { id: rawId } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const id = rawId ? Number(String(rawId).trim()) : NaN

  const catalogQuery = useQuery({
    queryKey: ['counsel-surveys', 'catalog'],
    queryFn: async () => unwrap(await api.get('/counsel-surveys/catalog', { __silent: true })),
  })

  const myQuery = useQuery({
    queryKey: ['counsel-surveys', 'my-templates', 'detail', id],
    enabled: Number.isFinite(id) && id > 0,
    queryFn: async () => unwrap(await api.get(`/counsel-surveys/my-templates/${id}`, { __silent: true })),
  })

  const catalog = useMemo(
    () => (Array.isArray(catalogQuery.data) ? catalogQuery.data : []),
    [catalogQuery.data],
  )

  const my = myQuery.data

  const catalogItem = useMemo(() => {
    if (!my?.baseTemplateId) return null
    return catalog.find((c) => c?.template?.id === my.baseTemplateId) || null
  }, [catalog, my])

  const baseSchema = catalogItem?.template?.schema || null

  const editorItem = useMemo(() => {
    if (!my) return null
    return {
      category: {
        code: my.categoryCode || 'CUSTOM',
        name: catalogItem?.category?.name || '맞춤 설문',
      },
      template: catalogItem?.template || { id: my.baseTemplateId, schema: baseSchema || {}, name: '' },
      myTemplate: { id: my.id, name: my.name, schema: my.schema },
      ready: true,
    }
  }, [my, catalogItem, baseSchema])

  const catalogImportSources = useMemo(() => {
    const mine = String(catalogItem?.category?.code || '')
    return catalog
      .filter((x) => x?.ready && x?.template?.schema && String(x?.category?.code || '') !== mine)
      .map((x) => ({
        code: x.category.code,
        categoryName: x.category.name,
        schema: x.template.schema,
      }))
  }, [catalog, catalogItem])

  const saveMutation = useMutation({
    mutationFn: async ({ name, schema }) => unwrap(await api.patch(`/counsel-surveys/my-templates/${id}`, {
      name,
      schema,
      publish: true,
      changeSummary: '맞춤 설문 편집',
    })),
    onSuccess: async () => {
      try { window?.toast?.show?.('설문 구성을 저장했습니다.', 'success') } catch {}
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'catalog'] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'my-templates', 'list'] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'my-templates', 'detail', id] }),
      ])
      navigate('/support/surveys?tab=list')
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '저장 실패', 'error') } catch {}
    },
  })

  if (!Number.isFinite(id) || id <= 0) {
    return <InlineError message="올바른 설문 ID가 아닙니다." />
  }
  if (catalogQuery.isLoading || myQuery.isLoading) return <Loading />
  if (catalogQuery.isError) return <InlineError message="설문 목록을 불러오지 못했습니다." />
  if (myQuery.isError || !my) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12 text-center">
        <p className="text-slate-600">해당 맞춤 설문을 찾을 수 없습니다.</p>
        <Link to="/support/surveys?tab=list" className="mt-4 inline-block rounded-lg border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50">
          설문관리로
        </Link>
      </div>
    )
  }

  return (
    <div className="pb-10">
      <div className="mb-6">
        <div className="text-sm text-slate-500">
          <Link to="/support/surveys?tab=list" className="hover:text-brand-sky">설문관리</Link>
          <span className="mx-2 text-slate-300">/</span>
          <span className="text-slate-700">맞춤 설문 편집</span>
        </div>
        <h1 className="mt-3 text-2xl font-bold text-slate-900">맞춤 설문 편집</h1>
        <p className="mt-1 text-sm text-slate-500">{my.name || `설문 #${id}`}</p>
      </div>

      {editorItem && (
        <SurveyTemplateEditor
          item={editorItem}
          baseSchema={baseSchema}
          catalogImportSources={catalogImportSources}
          onCancel={() => navigate('/support/surveys?tab=list')}
          onSave={(payload) => saveMutation.mutate(payload)}
          saving={saveMutation.isPending}
        />
      )}
    </div>
  )
}
